import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'

// antd
import {
    Avatar,
    Image,
    Modal,
    Row,
    Select,
    Spin,
    Tooltip,
    Typography,
    message,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'

// slices
import { searchUsers, usersList, usersState } from '../users/users.slice'
import { loginState } from '../login/login.slice'
import {
    changePage,
    teamListGet,
    teamMemberAddRemove,
    teamState,
    totalTeam,
} from './team.slice'

// components
import Button from '../../components/button/button'

// assets
import { CloseModalIcon, CrossIcon } from '../../assets/icons'

function TeamModalContent({ visibility, onCancel }) {
    const { Title } = Typography
    const { Option } = Select
    const dispatch = useDispatch()

    const { status, users, search: userSearch } = useSelector(usersState)
    const {
        pageLimit,
        skipPage,
        search,
        teamList,
        status: teamStatus,
    } = useSelector(teamState)
    const { userProfile } = useSelector(loginState)

    const [value, setValue] = useState([])
    const [options, setOptions] = useState([])
    const [selectedMap, setSelectedMap] = useState({})
    const [memberLoadingButton, setMemberLoadingButton] = useState(false)
    const [loadingButton] = useState(false)

    // ADD TEAM MEMBER
    async function addTeamMember() {
        setMemberLoadingButton(true)
        const newValue = value?.length > 0 && value.map((data) => data?.value)
        const result = await dispatch(
            teamMemberAddRemove({
                id: userProfile?.activeProject?._id,
                member: {
                    fieldName: 'add',
                    teamMembers: [...newValue],
                },
            })
        )
        const data = result?.payload?.data
        if (data) {
            const { success, message: checkMessage } = data
            if (success) {
                setMemberLoadingButton(false)
                message.success(checkMessage)
                dispatch(searchUsers(''))
                dispatch(
                    totalTeam({
                        search: search,
                        projectId: userProfile?.activeProject?._id,
                    })
                )
                dispatch(
                    teamListGet({
                        limit: pageLimit,
                        skip: skipPage,
                        search: search,
                        projectId: userProfile?.activeProject?._id,
                    })
                )
                dispatch(
                    changePage({
                        currentPage: 1,
                        skip: 0,
                        pageLimit,
                    })
                )
                onCancel()
                setValue([])
                setSelectedMap({})
            } else {
                setMemberLoadingButton(false)
                message.error(checkMessage || 'Something went wrong, try again later.')
                onCancel()
            }
        }
    }

    useEffect(() => {
        if (users?.length > 0) {
            let filteredUsers = users
            if (teamList?.length > 0) {
                filteredUsers = users.filter(
                    (user) => !teamList.some((team) => team?._id === user?._id)
                )
            }

            if (filteredUsers?.length > 0) {
                const dataOptions = filteredUsers.map((data) => ({
                    label: (
                        <Row align="middle" style={{ columnGap: '10px' }}>
                            {data?.profilePicture ? (
                                <Image
                                    src={data?.profilePicture}
                                    height={22}
                                    width={22}
                                    style={{ borderRadius: '50%' }}
                                    preview={false}
                                />
                            ) : (
                                <Avatar
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: 'rgb(0 10 129 / 25%)',
                                        textTransform: 'capitalize',
                                    }}
                                    size={22}
                                >
                                    {data?.name?.[0]?.toUpperCase()}
                                    {data?.name?.split(' ')[1]?.[0]?.toUpperCase()}
                                </Avatar>
                            )}
                            {data?.name}
                        </Row>
                    ),
                    textLabel: data?.name,
                    value: data?._id,
                    role: data?.role?.roleName,
                    profilePicture: data?.profilePicture,
                }))
                setOptions(dataOptions)

                // update selectedMap for new users
                const updatedMap = { ...selectedMap }
                dataOptions.forEach((opt) => {
                    if (!updatedMap[opt.value]) {
                        updatedMap[opt.value] = opt
                    }
                })
                setSelectedMap(updatedMap)
            } else if (userSearch?.length > 0) {
                setOptions([])
                message.info('This member is already in your team')
            }
        } else {
            setOptions([])
            if (teamStatus === 'loaded' && userSearch?.length > 0) {
                message.info('No User Found')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users])

    // SEARCH USERS
    const searchFn = debounce((value) => {
        if (value?.length > 0) {
            dispatch(searchUsers(value))
            dispatch(
                usersList({
                    search: value,
                    skip: 0,
                    filter: {
                        isAssigned: true,
                    },
                })
            )
        }
    }, 1000)

    return (
        <Modal
            closable={true}
            open={visibility}
            onCancel={() => {
                onCancel()
                setOptions([])
                dispatch(searchUsers(''))
                setValue([])
                setSelectedMap({})
            }}
            destroyOnClose={true}
            width={550}
            confirmLoading={loadingButton}
            footer={null}
            centered
            title={
                <>
                    <Row align="middle" justify="start" style={{ columnGap: '10px' }}>
                        <Title level={4} style={{ margin: 0, fontWeight: '700' }}>
                            Invite Teammates
                        </Title>
                        <Tooltip title="Only Assigned user can be in the list">
                            <InfoCircleOutlined />
                        </Tooltip>
                    </Row>
                    <Row align="middle" justify="start" style={{ marginTop: '5px' }}>
                        <Title style={{ margin: 0, fontSize: '14px', color: '#7c7c7c' }}>
                            Invite your team to review collaborate on this project.
                        </Title>
                    </Row>
                </>
            }
            styles={{ body: { padding: '10px 0px 0' } }}
        >
            <Row align="middle" style={{ flexFlow: 'row nowrap' }}>
                <Select
                    mode="multiple"
                    value={value}
                    removeIcon={
                        <div style={{ height: '14px', width: '14px' }}>
                            <CloseModalIcon />
                        </div>
                    }
                    className="add_member_model"
                    placeholder="Search"
                    filterOption={false}
                    onSearch={searchFn}
                    disabled={memberLoadingButton}
                    notFoundContent={status === 'loading' ? <Spin size="small" /> : null}
                    loading={status === 'loading'}
                    onChange={(_, selected) => {
                        setValue(selected)
                        const updatedMap = { ...selectedMap }
                        selected.forEach((sel) => {
                            if (!updatedMap[sel.value]) {
                                const match = options.find((opt) => opt.value === sel.value)
                                if (match) updatedMap[sel.value] = match
                            }
                        })
                        setSelectedMap(updatedMap)
                    }}
                    tagRender={(props) => {
                        const { closable, onClose, value: val } = props
                        const selectedUser = selectedMap[val]

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    background: '#f5f5f5',
                                    borderRadius: 10,
                                    padding: '4px 8px 4px 4px',
                                    margin: '4px 2px',
                                    maxWidth: '100%',
                                    border: "1px solid #afafaf80"
                                }}
                            >
                                {selectedUser?.profilePicture ? (
                                    <Avatar
                                        size={25}
                                        src={selectedUser.profilePicture}
                                        style={{ marginRight: 6 }}
                                    />
                                ) : (
                                    <Avatar
                                        size={25}
                                        style={{
                                            backgroundColor: 'rgb(0 10 129 / 25%)',
                                            color: '#000',
                                            fontSize: 10,
                                            marginRight: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {selectedUser?.textLabel?.[0]?.toUpperCase()}
                                        {selectedUser?.textLabel?.split(' ')[1]?.[0]?.toUpperCase()}
                                    </Avatar>
                                )}
                                <span
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: '#333',
                                        marginRight: 4,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 100,
                                    }}
                                >
                                    {selectedUser?.textLabel}
                                </span>
                                {closable && (
                                    <span onClick={onClose} style={{ cursor: 'pointer', display: "flex" }}>
                                        <CrossIcon width={10} color={"#252525"} />
                                    </span>
                                )}
                            </div>
                        )
                    }}
                    rootClassName="p-0-multiSelect"
                    style={{ width: '100%', padding: '0px' }}
                >
                    {options?.map((data) => (
                        <Option key={data.value} value={data.value} label={data.label}>
                            {data.label}
                        </Option>
                    ))}
                </Select>
            </Row>

            <Row style={{ marginTop: '20px', justifyContent: 'end', columnGap: '10px' }}>
                <Button
                    props={{
                        text: 'Cancel',
                        onClick: () => {
                            onCancel()
                            setOptions([])
                            dispatch(searchUsers(''))
                            setValue([])
                            setSelectedMap({})
                        },
                        style: {
                            color: '#000',
                            backgroundColor: '#fff',
                            border: '1px solid rgb(217, 217, 217)',
                        },
                    }}
                />
                <Button
                    props={{
                        text: 'Invite',
                        disabled: value?.length === 0,
                        loadingButton: memberLoadingButton,
                        onClick: addTeamMember,
                    }}
                />
            </Row>
        </Modal>
    )
}

export default TeamModalContent
